import React, { useState, useEffect, useMemo } from "react";
import "./styles.css";
import RoomCard from "../RoomCard";
import Button from "../../components/Button";
import CheckIcon from "../../components/CheckIcon";
import Switch from "../../components/Switch";
import { validatePdb } from "./utils";

const engines = [
  {
    name: "HandMol Viewer",
    key: "VIEWONLY",
    description: "Regular molecular graphics with option for AR/VR. Supports all elements. No simulation.",
    imgUrl: "https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/visonly.png",
  },
  {
    name: "Amber14 iMD",
    key: "OPENMM",
    description: "Interactive MD in AR/VR with automated AMBER14 or custom prmtop + inpcrd files.",
    imgUrl: "https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/amber.png",
  },
  {
    name: "ANI (DFT-AI) iMD",
    key: "ANI",
    description: "Interactive MD in AR/VR with ANI-2x. Supports H, C, N, O, S, F, Cl.",
    imgUrl: "https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/ani.png",
  },
  {
    name: "Rigid-body iMD",
    key: "CANNON",
    description: "Interactive MD with a rigid-body approximation. Supports all elements, custom interactions and restrained atoms.",
    imgUrl: "https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/cannon.png",
  },
  {
    name: "Calvados iMD",
    key: "CALVADOS",
    description: "Interactive MD with Calvados CG forcefield. Supports proteins in solution or in membranes.",
    imgUrl: "https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/calvados.png",
  },
];

const HandmolMenu = ({ availablePdbs, onRequestNewRoom }) => {
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [bMembrane, setBMembrane] = useState(false);
  const [withCannon, setWithCannon] = useState(false);
  const [simType, setSimType] = useState("");
  const [pdbCode, setPdbCode] = useState("");
  const [pdb, setPdb] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const [top, setTop] = useState("");
  const [topName, setTopName] = useState("");
  const [crd, setCrd] = useState("");
  const [crdName, setCrdName] = useState("");
  const [domains, setDomains] = useState("");
  const [chargeScaling, setChargeScaling] = useState("");
  const [amberpH, setAmberpH] = useState("");
  const [refHead, setRefHead] = useState("");

  const calvadosPdbs = useMemo(() => {
    if (availablePdbs) {
      return availablePdbs.filter((molecule) => molecule.engines.includes("CALVADOS"));
    } else {
      return [];
    }
  }, [availablePdbs]);

  const aniPdbs = useMemo(() => {
    if (availablePdbs) {
      return availablePdbs.filter((molecule) => molecule.engines.includes("ANI"));
    } else {
      return [];
    }
  }, [availablePdbs]);

  const cannonPdbs = useMemo(() => {
    if (availablePdbs) {
      return availablePdbs.filter((molecule) => molecule.engines.includes("CANNON"));
    } else {
      return [];
    }
  }, [availablePdbs]);

  const openMMPdbs = useMemo(() => {
    if (availablePdbs) {
      return availablePdbs.filter((molecule) => molecule.engines.includes("OPENMM"));
    } else {
      return [];
    }
  }, [availablePdbs]);

  const viewonlyMMPdbs = useMemo(() => {
    if (availablePdbs) {
      return availablePdbs.filter((molecule) => molecule.engines.includes("VIEWONLY"));
    } else {
      return [];
    }
  }, [availablePdbs]);

  const pdbList = useMemo(() => {
    if (simType === "ANI") {
      return aniPdbs;
    } else if (simType === "CANNON") {
      return cannonPdbs;
    } else if (simType === "OPENMM") {
      return openMMPdbs;
    } else if (simType === "CALVADOS") {
      return calvadosPdbs;
    } else if (simType === "VIEWONLY") {
      return viewonlyMMPdbs;
    } else {
      return [];
    }
  }, [aniPdbs, cannonPdbs, openMMPdbs, simType]);

  const handleSimSelection = (type) => {
    setIsSettingUp(true);
    setSimType(type);
  };

  const handleSearch = (e) => {
    setIsSearching(true);

    const url = `https://files.rcsb.org/view/${pdbCode}.pdb`;

    fetch(url)
      .then((response) => {
        if (response.status === 404) {
          throw new Error("PDB not found");
        } else {
          return response.text();
        }
      })
      .then(function (data) {
        // Check if response is HTML (error page) instead of PDB data
        if (data.trim().toLowerCase().startsWith('<!doctype html') || data.trim().toLowerCase().startsWith('<html')) {
          throw new Error("PDB not found");
        }
        setIsSearching(false);
        setShowWarning(false);
        setPdb(data);
      })
      .catch(function (error) {
        console.log("Error: ", error);
        setIsSearching(false);
        setValidationMessage("PDB code not found. Please check the code and try again.");
        setShowWarning(true);
      });
  };

  const handleUpload = (e) => {
    const reader = new FileReader();
    reader.onload = () => {
      setPdb(reader.result);
    };
    reader.readAsText(e.target.files[0]);
  };

  const handleUploadTop = (e) => {
    const reader = new FileReader();
    reader.onload = () => {
      setTop(reader.result);
      setTopName(e.target.files[0].name);
    };
    reader.readAsText(e.target.files[0]);
  };

  const handleUploadCrd = (e) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCrd(reader.result);
      setCrdName(e.target.files[0].name);
    };
    reader.readAsText(e.target.files[0]);
  };

  const handleSubmitPDB = () => {
    const { isValid, msg } = validatePdb(pdb, simType);
    if (isValid) {
      if (simType === "CALVADOS") {
        onRequestNewRoom({ pdb, engine: simType, setupType: "PDB", refHead, domains, bMembrane });
      } else if (simType === "ANI") {
        onRequestNewRoom({ pdb, engine: simType, setupType: "PDB", withCannon });
      } else if (simType === "OPENMM") {
        onRequestNewRoom({ pdb, engine: simType, setupType: "PDB", chargeScaling, amberpH });
      } else if (simType === "CANNON") {
        onRequestNewRoom({ pdb, engine: simType, setupType: "PDB"});
      } else if (simType === "VIEWONLY") {
        //***VIEWONLY
        onRequestNewRoom({ pdb, engine: simType, setupType: "PDB" }); //***VIEWONLY
      }
    } else {
      setValidationMessage(msg);
      setShowWarning(true);
    }
  };

  const handleSubmitParams = () => {
    if (crd.length > 0 && top.length > 0) {
      onRequestNewRoom({ crd, top, engine: simType, setupType: "PARAMS", chargeScaling });
    }
  };

  const handlePDBChange = (e) => {
    setShowWarning(false);
    setPdb(e.target.value);
  };

  const handleDropdownChange = (e) => {
    setPdb(e.target.value);
    if (simType === "CALVADOS") {
      const selectedPdb = pdbList.find((pdb) => pdb.pdb === e.target.value);

      if (selectedPdb) {
        setRefHead(selectedPdb.refHead);
        setDomains(selectedPdb.domains);
        setBMembrane(selectedPdb.bMembrane);
      } else {
        setRefHead("");
        setDomains("");
        setBMembrane(false);
      }
    }
  };

  const handleInputChange = (e) => {
    setShowWarning(false);
    setPdbCode(e.target.value);
  };

  if (isSettingUp) {
    return (
      <div className="handmol-setup-container">
        <div className="handmol-engine-info row">
          <div className="handmol-engine-info-left row">
            <CheckIcon variant="purple-icon" />
            <div className="column">
              <p>{engines.find((engine) => engine.key === simType)?.name || simType}</p>
              <p>Session type selected</p>
            </div>
          </div>
          <Button handleClick={() => setIsSettingUp(false)}>Change</Button>
        </div>
        <h3 className="create-subtitle">OPTION 1: FROM INPUT COORDINATES IN PDB FORMAT</h3>
        <p className="create-menu-text">
          Copy-paste your atomic coordinates in PDB format, or upload a PDB file from your drive, or fetch a protein
          structure/model from the Protein Data Bank, and click Submit which will automatically parametrize the system with ff14SB. Supports proteins, DNA, RNA, water and common ff14SB-related ions and small molecules.
        </p>
        <p className="create-menu-text"></p>
        <br></br>
        <p className="create-menu-text">
          Hints: In Amber and rigid-body simulations, an F to the left of the dot in the B-factor column (for example F.00) restraints the atom to its starting position.<br></br>Additionally, you can encode interactions in the rigid-body simulations by placing + and - signs at the dot position of the B-factor column.
        </p>

        <div className="handmol-dropdown-container row">
          <label className="dropdown-label">Examples:</label>
          <select className="handmol-dropdown" onChange={handleDropdownChange}>
            <option value="">Some example PDBs here for a quick test</option>
            {pdbList &&
              pdbList.map((example) => (
                <option key={example.name} value={example.pdb}>
                  {example.name}
                </option>
              ))}
          </select>
        </div>
        <div className="handmol-search-container" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="text"
            className="handmol-search-input"
            placeholder="PDB ID, for ex. 1UBI"
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            value={pdbCode}
            style={{ width: "150px" }} //, height: "40px", fontSize: "16px" }}
          />
          <Button className="pdb-action-btn" handleClick={handleSearch} loading={isSearching}>
            Fetch PDB
          </Button>
          <Button className="pdb-action-btn" handleClick={handleUpload} variant="input" extensions=".pdb">
            Upload PDB...
          </Button>
          <Button className="pdb-action-btn" handleClick={handleSubmitPDB} disabled={pdb.length === 0}>
            Submit
          </Button>
        </div>
        <textarea
          className="handmol-pdb-text"
          cols="30"
          rows="7"
          placeholder="Fetch a PDB, or upload a PDB file, or paste a PDB here (regular PDB format --see examples)"
          onChange={handlePDBChange}
          value={pdb}
          style={{ fontFamily: '"Courier New", Courier, monospace' }}
        />

        {simType === "CALVADOS" && (
          <div className="calvados-params-container column">
            <p className="create-menu-text">Provide the following parameters to setup the simulation</p>
            <div className="param-input-container row">
              <p className="label-text dark">Domains:</p>
              <input
                type="text"
                className="handmol-search-input"
                label="Domains: "
                placeholder="A:5-20,50-70; B:3-35,80-103,166-187"
                onChange={(e) => setDomains(e.target.value)}
                value={domains}
              />
            </div>
            <div className="switch-container row">
              <p className="label-text dark">With membrane?</p>
              <Switch
                id="controls"
                isEnabled={bMembrane}
                handleClick={(e) => {
                  setBMembrane(!bMembrane);
                }}
              />
            </div>
            <div className="param-input-container row">
              <p className="label-text dark">Ref. bead for membrane insertion:</p>
              <input
                type="text"
                className="handmol-search-input"
                placeholder="9"
                onChange={(e) => setRefHead(e.target.value)}
                value={refHead}
              />
            </div>
          </div>
        )}

        {showWarning && <p className="text-description error">{validationMessage}</p>}

        {simType === "ANI" && (
          <div className="switch-container row ani">
            <p className="label-text dark">With rigid-body physics</p>
            <Switch
              id="controls"
              isEnabled={withCannon}
              handleClick={(e) => {
                setWithCannon(!withCannon);
              }}
            />
          </div>
        )}

        {simType === "OPENMM" && (
          <div className="params-container column">
            <hr></hr>
            <h3 className="create-subtitle">OPTION 2: USING YOUR OWN PARAMETERS IN AMBER FORMAT</h3>
            <p className="create-menu-text">
              Upload your own Amber files (created with tLeap, CHARMM-GUI, <a download href="https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/CharmmToAmber.zip">converted from CHARMM</a>, etc.) Find <a href="https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/custom_amber_examples.zip" download>examples here.</a>
            </p>
            <div className="param-input-box row">
              <Button
                className="pdb-action-btn"
                handleClick={handleUploadTop}
                variant="input"
                extensions=".top, .prmtop, .parm7"
              >
                Upload top...
              </Button>
              <p>{topName}</p>
            </div>
            <div className="param-input-box row">
              <Button
                className="pdb-action-btn"
                handleClick={handleUploadCrd}
                variant="input"
                extensions=".inpcrd, .restrt, .rst, .rst7"
              >
                Upload crd...
              </Button>
              <p>{crdName}</p>
            </div>
            <Button className="pdb-action-btn" handleClick={handleSubmitParams}>
              Submit custom Amber files
            </Button>
            <hr></hr>
            <p className="create-menu-text"><b>ADDITIONAL (OPTIONAL) PARAMETERS</b></p>
            <div className="param-input-container row">
              <p className="label-text dark">pH:</p>
              <input
                type="text"
                className="handmol-search-input"
                label="pH: "
                placeholder="(default = 7.0)"
                onChange={(e) => setAmberpH(e.target.value)}
                value={amberpH}
              />
              (applies only to automatic parametrization)
            </div>
            <div className="param-input-container row">
              <p className="label-text dark">Charge scaling:</p>
              <input
                type="text"
                className="handmol-search-input"
                label="Charge scaling: "
                placeholder="(default = 0.5; rec. = 1 with explicit waters)"
                onChange={(e) => setChargeScaling(e.target.value)}
                value={chargeScaling}
              />
              (applies to all)
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <p className="create-menu-text">
        HandMol is our set of immersive multiuser molecular graphics tools, additionally supported by interactive
        simulations.
      </p>
      <div className="cards-container">
        {engines.map((engine) => (
          <RoomCard
            key={engine.key}
            handleClick={() => handleSimSelection(engine.key)}
            name={engine.name}
            description={engine.description}
            imgUrl={engine.imgUrl}
          />
        ))}
      </div>
    </>
  );
};
export default HandmolMenu;